import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import ptCommon from "@/locales/pt/common.json";
import ptNav from "@/locales/pt/nav.json";
import ptAuth from "@/locales/pt/auth.json";
import ptValidation from "@/locales/pt/validation.json";
import ptErrors from "@/locales/pt/errors.json";
import ptCalendar from "@/locales/pt/calendar.json";
import ptSettings from "@/locales/pt/settings.json";
import ptReports from "@/locales/pt/reports.json";
import ptDataExclusion from "@/locales/pt/dataExclusion.json";
import ptPatients from "@/locales/pt/patients.json";
import ptMedicalRecords from "@/locales/pt/medicalRecords.json";
import ptErp from "@/locales/pt/erp.json";
import ptGrooming from "@/locales/pt/grooming.json";
import ptAdmin from "@/locales/pt/admin.json";
import ptBackoffice from "@/locales/pt/backoffice.json";
import ptPrescriptions from "@/locales/pt/prescriptions.json";
import ptTerms from "@/locales/pt/terms.json";
import ptLanding from "@/locales/pt/landing.json";
import ptWhatsappTemplates from "@/locales/pt/whatsappTemplates.json";
import ptCampaigns from "@/locales/pt/campaigns.json";
import ptHospitalization from "@/locales/pt/hospitalization.json";
import ptDaycareHotel from "@/locales/pt/daycareHotel.json";
import ptPetVet from "@/locales/pt/petVet.json";
import ptFinancial from "@/locales/pt/financial.json";
import ptTeam from "@/locales/pt/team.json";
import ptCommissions from "@/locales/pt/commissions.json";

import enCommon from "@/locales/en/common.json";
import enNav from "@/locales/en/nav.json";
import enAuth from "@/locales/en/auth.json";
import enValidation from "@/locales/en/validation.json";
import enErrors from "@/locales/en/errors.json";
import enCalendar from "@/locales/en/calendar.json";
import enSettings from "@/locales/en/settings.json";
import enReports from "@/locales/en/reports.json";
import enDataExclusion from "@/locales/en/dataExclusion.json";
import enPatients from "@/locales/en/patients.json";
import enMedicalRecords from "@/locales/en/medicalRecords.json";
import enErp from "@/locales/en/erp.json";
import enGrooming from "@/locales/en/grooming.json";
import enAdmin from "@/locales/en/admin.json";
import enBackoffice from "@/locales/en/backoffice.json";
import enPrescriptions from "@/locales/en/prescriptions.json";
import enTerms from "@/locales/en/terms.json";
import enLanding from "@/locales/en/landing.json";
import enWhatsappTemplates from "@/locales/en/whatsappTemplates.json";
import enCampaigns from "@/locales/en/campaigns.json";
import enHospitalization from "@/locales/en/hospitalization.json";
import enDaycareHotel from "@/locales/en/daycareHotel.json";
import enPetVet from "@/locales/en/petVet.json";
import enFinancial from "@/locales/en/financial.json";
import enTeam from "@/locales/en/team.json";
import enCommissions from "@/locales/en/commissions.json";

const CORE_NAMESPACES = [
  "common",
  "nav",
  "auth",
  "validation",
  "errors",
  "calendar",
  "settings",
  "reports",
  "dataExclusion",
  "patients",
  "medicalRecords",
] as const;

// Domain flows should keep keys inside their own namespace to avoid growing
// catch-all files such as `common` and to make phased i18n work traceable.
const DOMAIN_NAMESPACES = [
  "erp",
  "grooming",
  "admin",
  "backoffice",
  "prescriptions",
  "terms",
  "landing",
  "whatsappTemplates",
  "campaigns",
  "hospitalization",
  "daycareHotel",
  "petVet",
  "financial",
  "team",
  "commissions",
] as const;

const NAMESPACES = [...CORE_NAMESPACES, ...DOMAIN_NAMESPACES] as const;

const resources = {
  pt: {
    common: ptCommon,
    nav: ptNav,
    auth: ptAuth,
    validation: ptValidation,
    errors: ptErrors,
    calendar: ptCalendar,
    settings: ptSettings,
    reports: ptReports,
    dataExclusion: ptDataExclusion,
    patients: ptPatients,
    medicalRecords: ptMedicalRecords,
    erp: ptErp,
    grooming: ptGrooming,
    admin: ptAdmin,
    backoffice: ptBackoffice,
    prescriptions: ptPrescriptions,
    terms: ptTerms,
    landing: ptLanding,
    whatsappTemplates: ptWhatsappTemplates,
    campaigns: ptCampaigns,
    hospitalization: ptHospitalization,
    daycareHotel: ptDaycareHotel,
    petVet: ptPetVet,
    financial: ptFinancial,
    team: ptTeam,
    commissions: ptCommissions,
  },
  en: {
    common: enCommon,
    nav: enNav,
    auth: enAuth,
    validation: enValidation,
    errors: enErrors,
    calendar: enCalendar,
    settings: enSettings,
    reports: enReports,
    dataExclusion: enDataExclusion,
    patients: enPatients,
    medicalRecords: enMedicalRecords,
    erp: enErp,
    grooming: enGrooming,
    admin: enAdmin,
    backoffice: enBackoffice,
    prescriptions: enPrescriptions,
    terms: enTerms,
    landing: enLanding,
    whatsappTemplates: enWhatsappTemplates,
    campaigns: enCampaigns,
    hospitalization: enHospitalization,
    daycareHotel: enDaycareHotel,
    petVet: enPetVet,
    financial: enFinancial,
    team: enTeam,
    commissions: enCommissions,
  },
};

const LOCALE_STORAGE_KEY = "app-locale";
const USED_LANGUAGE_STORAGE_KEY = "used-language";

const getInitialLanguage = (): "pt" | "en" => {
  if (typeof window === "undefined") return "pt";
  const savedLanguage =
    window.localStorage.getItem(USED_LANGUAGE_STORAGE_KEY) ??
    window.localStorage.getItem(LOCALE_STORAGE_KEY);

  if (savedLanguage?.startsWith("en")) return "en";
  return "pt";
};

void i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: "pt",
    defaultNS: "common",
    ns: [...NAMESPACES],
    interpolation: {
      escapeValue: false,
    },
  });

i18n.on("languageChanged", (language) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(USED_LANGUAGE_STORAGE_KEY, language);
  window.localStorage.setItem(LOCALE_STORAGE_KEY, language);
});

export default i18n;
